// src/pages/SalesCRM/DealsPage.tsx
// Сделки — канбан с drag&drop, карточка, фильтры, CRUD
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FileText, Paperclip, Download, Upload,
  Plus, Search, RefreshCw, X, Filter, Building2, Phone, Mail,
  Calendar, Clock, CheckCircle, AlertCircle, MoreVertical, Trash2,
  Edit, ArrowRight, Flame, Star, Zap, MessageSquare, List, Kanban,
  ChevronDown, User, TrendingUp
} from 'lucide-react';
import api from '../../api/client';
import EmailThreadPanel from '../../components/EmailThreadPanel';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useToast } from '../../contexts/ToastContext';
import ExportButton from '../../components/ExportButton';
import SendDealModal from './components/SendDealModal';



interface Stage { key: string; title: string; order: number; color: string; icon: string; probability: number; type: string }
interface Deal {
  _id: string; title: string; clientName?: string; clientCompany?: string;
  clientEmail?: string; clientPhone?: string; amount: number; probability: number;
  stageKey: string; status: string; priority: string; expectedCloseDate?: string;
  ownerName?: string; source?: string; tags?: string[]; createdAt?: string;
  processStatus?: string;
  unreadEmailCount?: number;
}

const PRIORITY_META: Record<string, { label: string; color: string; icon: any }> = {
  low: { label: 'Низкий', color: '#94a3b8', icon: Star },
  normal: { label: 'Обычный', color: '#2575fc', icon: Star },
  high: { label: 'Высокий', color: '#f59e0b', icon: Flame },
  urgent: { label: 'Срочный', color: '#ef4444', icon: Zap },
};

const SOURCE_LABELS: Record<string, string> = {
  website: '🌐 Сайт', call: '📞 Звонок', email: '📧 Email',
  referral: '👥 Рекомендация', social: '📱 Соцсети', ad: '📢 Реклама',
  event: '🎪 Мероприятие', cold: '❄️ Холодный', other: '📌 Другое',
};

const DealsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const { showConfirm } = useConfirm();

  const [view, setView] = useState<'board' | 'list'>('board');
  const [loading, setLoading] = useState(true);
  const [pipeline, setPipeline] = useState<any>(null);
  const [stages, setStages] = useState<Stage[]>([]);
  const [board, setBoard] = useState<Record<string, Deal[]>>({});
  const [totals, setTotals] = useState<Record<string, { count: number; sum: number }>>({});
  const [totalSum, setTotalSum] = useState(0);
  const [totalDeals, setTotalDeals] = useState(0);

  const [search, setSearch] = useState('');
  const [boards, setBoards] = useState<any[]>([]);
  const [activeBoardId, setActiveBoardId] = useState<string>(() => {
    try { return localStorage.getItem('sales-active-board') || 'default'; }
    catch { return 'default'; }
  });
  const [customBoardData, setCustomBoardData] = useState<any>(null);
  const [filterStage, setFilterStage] = useState('');
  const [filterOwner, setFilterOwner] = useState('');
  const [filterPriority, setFilterPriority] = useState('');

  const [selectedDeal, setSelectedDeal] = useState<any>(null);

  // ★ CSS-анимация для badge «НОВОЕ»
  useEffect(() => {
    if (document.getElementById('deals-anim-styles')) return;
    const s = document.createElement('style');
    s.id = 'deals-anim-styles';
    s.textContent = '@keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.85; transform: scale(1.05); } }';
    document.head.appendChild(s);
  }, []);

  // ★ Пометить сделку как просмотренную
  const markAsViewed = useCallback(async (deal: any) => {
    if (!deal || !deal._id) return;
    const dealId = String(deal._id);

    // ★ СНАЧАЛА локально — badge исчезает мгновенно
    setBoard((prev: any) => {
      const next: any = {};
      Object.keys(prev).forEach(k => {
        next[k] = (prev[k] || []).map((d: any) => String(d._id) === dealId ? { ...d, isNew: false } : d);
      });
      return next;
    });

    setCustomBoardData((prev: any) => {
      if (!prev?.boardData) return prev;
      const nextBd: any = {};
      Object.keys(prev.boardData).forEach(k => {
        nextBd[k] = (prev.boardData[k] || []).map((d: any) => String(d._id) === dealId ? { ...d, isNew: false } : d);
      });
      return { ...prev, boardData: nextBd };
    });

    // ★ Только если был новым — идём на backend
    if (!deal.isNew) return;
    try {
      await api.post(`/sales/deals/${dealId}/mark-viewed`);
    } catch (e) { /* silent */ }
  }, []);

  // ★ Обработчик клика — mark-viewed + открытие
  const handleDealClick = useCallback((deal: any) => {
    markAsViewed(deal);
    setSelectedDeal(deal);
  }, [markAsViewed]);

  // ★ Обновляет unreadEmailCount на карточке сделки в board и customBoardData
  const handleUnreadChange = useCallback((targetDealId: string, cnt: number) => {
    const upd = (list: any[]) => (list || []).map((d: any) =>
      String(d._id) === String(targetDealId) ? { ...d, unreadEmailCount: cnt } : d
    );
    setBoard((prev: any) => {
      const next: any = {};
      Object.keys(prev).forEach(k => { next[k] = upd(prev[k]); });
      return next;
    });
    setCustomBoardData((prev: any) => {
      if (!prev?.boardData) return prev;
      const nextBd: any = {};
      Object.keys(prev.boardData).forEach(k => { nextBd[k] = upd(prev.boardData[k]); });
      return { ...prev, boardData: nextBd };
    });
  }, []);

  // ★ Открытие сделки по ?open=<id> (из уведомлений)
  useEffect(() => {
    const checkOpen = async () => {
      const params = new URLSearchParams(window.location.search);
      const openId = params.get('open');
      if (!openId) return;

      try {
        const r = await api.get(`/sales/deals/${openId}`);
        const deal = r.data?.deal || r.data;
        if (deal && deal._id) {
          setSelectedDeal(deal);
          // ★ Сразу помечаем просмотренным — badge исчезает
          markAsViewed(deal);
        }
      } catch (e) { /* silent */ }
      // Убираем ?open из URL чтобы не зацикливаться
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    };

    checkOpen();
    // ★ Слушаем изменения URL (navigate из уведомления)
    window.addEventListener('popstate', checkOpen);
    // Слушаем кастомное событие от колокольчика
    const handler = (e: any) => { checkOpen(); };
    window.addEventListener('sales:open-deal', handler);
    return () => {
      window.removeEventListener('popstate', checkOpen);
      window.removeEventListener('sales:open-deal', handler);
    };
  }, []);
  const [sendDealModal, setSendDealModal] = useState<any>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedColumns, setExpandedColumns] = useState<Set<string>>(new Set());
  const toggleColumn = useCallback((key: string) => {
    setExpandedColumns(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const [showFilters, setShowFilters] = useState(false);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);

  // Слушаем кастомное событие от DealDetailModal
  useEffect(() => {
    const handler = (e: any) => setSendDealModal(e.detail);
    window.addEventListener('send-deal-to', handler);
    return () => window.removeEventListener('send-deal-to', handler);
  }, []);

  const loadBoard = useCallback(async (silent: boolean = false) => {
    if (!silent) setLoading(true);
    try {
      const params: any = {};
      if (search) params.search = search;
      if (filterOwner) params.ownerId = filterOwner;
      const r = await api.get('/sales/deals/board', { params });
      setPipeline(r.data.pipeline);
      setStages(r.data.stages || []);
      setBoard(r.data.board || {});
      setTotals(r.data.totals || {});
      setTotalDeals(r.data.totalDeals || 0);
      setTotalSum(r.data.totalSum || 0);
    } catch (e: any) {
      showToast(e?.response?.data?.error || 'Ошибка загрузки', 'error');
    } finally { if (!silent) setLoading(false); }
  }, [search, filterOwner, showToast]);

  // ★ Универсальная перезагрузка текущей доски (default или кастомной)
  const reloadCurrentBoard = useCallback(async () => {
    if (activeBoardId === 'default') {
      await loadBoard();
    } else {
      try {
        const params: any = {};
        if (search) params.search = search;
        if (filterOwner) params.ownerId = filterOwner;
        const r = await api.get(`/sales/boards/${activeBoardId}/board`, { params });
        setCustomBoardData(r.data);
      } catch (e) { console.error('reloadCurrentBoard error:', e); }
    }
  }, [activeBoardId, search, filterOwner, loadBoard]);

  useEffect(() => { loadBoard(); }, [loadBoard]);

  // ★ Polling каждые 30 сек — новые письма подтягиваются без мигания
  useEffect(() => {
    const t = setInterval(() => {
      // silent reload — без сброса loading, чтобы не мигало
      loadBoard(true);
    }, 30000);
    return () => clearInterval(t);
  }, [loadBoard]);

  // ★ Persist activeBoardId
  useEffect(() => {
    try { localStorage.setItem('sales-active-board', activeBoardId); } catch {}
  }, [activeBoardId]);

  // Загрузка списка канбанов
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/sales/boards');
        setBoards(r.data.items || []);
      } catch (e) { console.error(e); }
    })();
  }, []);

  // ★ Загрузка кастомной доски если выбрана (с search и ownerId)
  useEffect(() => {
    if (activeBoardId === 'default') {
      setCustomBoardData(null);
      return;
    }
    (async () => {
      try {
        const params: any = {};
        if (search) params.search = search;
        if (filterOwner) params.ownerId = filterOwner;
        const r = await api.get(`/sales/boards/${activeBoardId}/board`, { params });
        setCustomBoardData(r.data);
      } catch (e) { console.error(e); }
    })();
  }, [activeBoardId, search, filterOwner]);

  const handleMoveDeal = async (dealId: string, stageKey: string) => {
    try {
      const r = await api.post(`/sales/deals/${dealId}/move`, { stageKey });
      showToast('✅ Перемещено', 'success');
      await reloadCurrentBoard();

      // ★ Если переместили в финальный этап — предложить отправить в бухгалтерию/юристам
      const stage = stages.find(s => s.key === stageKey);
      const movedDeal = r.data?.deal;
      if (stage && stage.type === 'won' && movedDeal) {
        // Открываем модалку через 300мс чтобы UI успел обновиться
        setTimeout(() => setSendDealModal(movedDeal), 300);
      }
    } catch (e: any) { showToast('Ошибка', 'error'); }
  };

  // Drag & drop
  const handleDragStart = (e: React.DragEvent, dealId: string) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('dealId', dealId);
    setDragging(dealId);
  };
  const handleDragOver = (e: React.DragEvent, stageKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(stageKey);
  };
  const handleDragLeave = () => setDragOver(null);
  const handleDrop = async (e: React.DragEvent, stageKey: string) => {
    e.preventDefault();
    const dealId = e.dataTransfer.getData('dealId');
    setDragOver(null);
    setDragging(null);
    if (!dealId) return;
    await handleMoveDeal(dealId, stageKey);
  };

  // Фильтры для list view
  const allDeals = Object.values(board).flat();

  // ★ Список сделок зависит от активной доски
  const activeDeals = activeBoardId !== 'default' && customBoardData
    ? Object.values(customBoardData.boardData || {}).flat()
    : allDeals;

  // Этапы тоже зависят от доски
  const activeStages = activeBoardId !== 'default' && customBoardData
    ? (customBoardData.board?.columns || []).map((c: any) => ({
        key: c.key, title: c.title, color: c.color || '#2575fc',
        icon: c.icon || '📌', probability: 0, type: 'active',
      }))
    : stages;

  // ★ Кто может удалять: автор или админ
  const currentUserId = (user as any)?.id || (user as any)?._id;
  const currentUserRole = (user as any)?.role;
  const isAdmin = ['admin', 'superadmin', 'owner'].includes(currentUserRole);
  const canDeleteDeal = (deal: any) => {
    if (!deal) return false;
    if (isAdmin) return true;
    if (String(deal.ownerId) === String(currentUserId)) return true;
    if (String(deal.createdBy) === String(currentUserId)) return true;
    return false;
  };
  const filteredDeals = allDeals.filter(d => {
    if (filterStage && d.stageKey !== filterStage) return false;
    if (filterPriority && d.priority !== filterPriority) return false;
    return true;
  });

  // ★ Для кастомной доски — фильтруем activeDeals
  const filteredActiveDeals = activeDeals.filter((d: any) => {
    if (filterStage && d.stageKey !== filterStage) return false;
    if (filterPriority && d.priority !== filterPriority) return false;
    return true;
  });

  return (
    <div style={{ padding: 24, maxWidth: 1800, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: '#1a1a2e', letterSpacing: -0.5, display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            💼 Сделки
            {(() => {
              const allDeals = activeBoardId === 'default'
                ? Object.values(board).flat()
                : Object.values(customBoardData?.boardData || {}).flat();
              const totalNew = allDeals.filter((d: any) => d.isNew).length;
              return totalNew > 0 ? (
                <span style={{
                  background: '#ef4444', color: '#fff',
                  fontSize: 12, fontWeight: 800, padding: '4px 12px',
                  borderRadius: 12, letterSpacing: 0.3,
                  boxShadow: '0 4px 12px rgba(239,68,68,0.35)',
                  animation: 'pulse 2s infinite',
                }}>● {totalNew} новых</span>
              ) : null;
            })()}
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
            {totalDeals} сделок · {formatMoney(totalSum)} ₽
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* Переключатель вид */}
          <div style={{
            display: 'flex', gap: 2, padding: 3, borderRadius: 12,
            background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.7)',
          }}>
            <button onClick={() => setView('board')} style={viewBtn(view === 'board')}>
              <Kanban size={14} /> Канбан
            </button>
            <button onClick={() => setView('list')} style={viewBtn(view === 'list')}>
              <List size={14} /> Список
            </button>
          </div>
          <button onClick={() => loadBoard(false)} style={btnOutline}><RefreshCw size={14} /></button>
          <ExportButton
            module="deals"
            params={
              activeBoardId === 'default'
                ? (pipeline?._id ? { pipelineId: String(pipeline._id) } : {})
                : (customBoardData?.board?.pipelineId ? { pipelineId: String(customBoardData.board.pipelineId) } : {})
            }
          />
          <button onClick={() => setShowCreate(true)} style={btnPrimary}>
            <Plus size={14} /> Новая сделка
          </button>
        </div>
      </div>

      {/* Канбан-доска + Search + Filters */}
      <div style={{
        display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap',
        background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(20px)',
        padding: 12, borderRadius: 16, border: '1px solid rgba(255,255,255,0.7)',
      }}>
        {/* Выбор канбана */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Kanban size={16} color="#8b5cf6" />
          <select
            value={activeBoardId}
            onChange={(e) => setActiveBoardId(e.target.value)}
            style={{
              padding: '10px 14px', borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.8)',
              background: 'rgba(255,255,255,0.8)',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit', minWidth: 180,
            }}
          >
            <option value="default">📊 Основная воронка</option>
            {boards.map((b: any) => (
              <option key={b._id} value={b._id}>
                {b.icon || '📌'} {b.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ flex: 1, minWidth: 240, position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            type="text" placeholder="Поиск по названию, клиенту..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            style={{ ...inputStyle, paddingLeft: 40 }}
          />
        </div>
        <button onClick={() => setShowFilters(!showFilters)} style={btnOutline}>
          <Filter size={14} /> Фильтры
          {showFilters ? <ChevronDown size={12} style={{ transform: 'rotate(180deg)' }} /> : <ChevronDown size={12} />}
        </button>
      </div>

      {showFilters && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12,
          marginBottom: 20, padding: 16, borderRadius: 14,
          background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.7)',
        }}>
          <div>
            <label style={labelText}>Этап</label>
            <select value={filterStage} onChange={(e) => setFilterStage(e.target.value)} style={inputStyle}>
              <option value="">Все этапы</option>
              {activeStages.map((s: any) => <option key={s.key} value={s.key}>{s.icon} {s.title}</option>)}
            </select>
          </div>
          <div>
            <label style={labelText}>Приоритет</label>
            <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} style={inputStyle}>
              <option value="">Любой</option>
              {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div style={{ padding: 80, textAlign: 'center', color: '#64748b' }}>
          <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      ) : view === 'board' ? (
        activeBoardId === 'default' ? (
          <KanbanBoard
            stages={stages} board={board} totals={totals}
            filteredDeals={filteredDeals}
            expandedColumns={expandedColumns}
            onToggleColumn={toggleColumn}
            dragging={dragging} dragOver={dragOver}
            onDragStart={handleDragStart} onDragOver={handleDragOver}
            onDragLeave={handleDragLeave} onDrop={handleDrop}
            onDealClick={handleDealClick}
            canDeleteDeal={canDeleteDeal}
          />
        ) : customBoardData ? (
          <KanbanBoard
            stages={(customBoardData.board?.columns || []).map((c: any) => ({
              key: c.key,
              title: c.title,
              color: c.color || '#2575fc',
              icon: c.icon || '📌',
              probability: 0,
              type: 'active',
            }))}
            board={customBoardData.boardData || {}}
            filteredDeals={filteredActiveDeals}
            expandedColumns={expandedColumns}
            onToggleColumn={toggleColumn}
            totals={Object.fromEntries(
              Object.entries(customBoardData.boardData || {}).map(([k, arr]: any) => [
                k,
                { count: arr.length, sum: arr.reduce((sum: number, d: any) => sum + (d.amount || 0), 0) }
              ])
            )}
            dragging={dragging} dragOver={dragOver}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={async (e: any, stageKey: string) => {
              e.preventDefault();
              const dealId = e.dataTransfer.getData('dealId');
              if (!dealId) return;
              try {
                await api.post(`/sales/deals/${dealId}/move`, { stageKey });
                const r = await api.get(`/sales/boards/${activeBoardId}/board`);
                setCustomBoardData(r.data);
              } catch (err: any) {
                console.error('Move error:', err);
              }
              setDragOver(null);
            }}
            onDealClick={handleDealClick}
            canDeleteDeal={canDeleteDeal}
          />
        ) : (
          <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>Загрузка доски...</div>
        )
      ) : (
        <DealsTable
          deals={activeBoardId !== 'default' ? activeDeals : filteredDeals}
          stages={activeStages}
          onDealClick={handleDealClick}
          onDelete={(id: string) => {
            showConfirm({
              title: 'Удалить сделку?',
              message: 'Эта сделка будет удалена безвозвратно.',
              variant: 'danger',
              confirmText: 'Удалить',
              onConfirm: async () => {
                await api.delete(`/sales/deals/${id}`);
                showToast('Удалено', 'success');
                await reloadCurrentBoard();
              },
            });
          }}
        />
      )}

      {/* Modals */}
      {selectedDeal && (
        <DealDetailModal
          dealId={selectedDeal._id} stages={activeStages}
          onClose={() => setSelectedDeal(null)}
          onUpdate={loadBoard}
          onUnreadChange={handleUnreadChange}
        />
      )}
      {showCreate && (
        <CreateDealModal
          pipeline={
            activeBoardId !== 'default' && customBoardData?.board?.pipelineId
              ? { _id: customBoardData.board.pipelineId }
              : pipeline
          }
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            if (activeBoardId !== 'default') {
              api.get(`/sales/boards/${activeBoardId}/board`)
                .then(r => setCustomBoardData(r.data))
                .catch(() => {});
            } else {
              loadBoard();
            }
          }}
        />
      )}

      {sendDealModal && (
        <SendDealModal
          deal={sendDealModal}
          onClose={() => setSendDealModal(null)}
          onSuccess={() => { setSendDealModal(null); loadBoard(); }}
        />
      )}
    </div>
  );
};

// ─── KANBAN ───
const MAX_VISIBLE_DEALS = 10;

const KanbanBoard: React.FC<any> = ({ stages, board, totals, filteredDeals, expandedColumns, onToggleColumn, dragging, dragOver, onDragStart, onDragOver, onDragLeave, onDrop, onDealClick, onDelete, canDeleteDeal }) => (
  <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 12, alignItems: 'flex-start' }}>
    {stages.map((stage: Stage) => {
      const allDeals = filteredDeals
        ? filteredDeals.filter((d: any) => d.stageKey === stage.key)
        : (board[stage.key] || []);
      const isExpanded = expandedColumns?.has?.(stage.key);
      const visibleDeals = isExpanded ? allDeals : allDeals.slice(0, MAX_VISIBLE_DEALS);
      const hiddenCount = allDeals.length - MAX_VISIBLE_DEALS;
      const t = totals[stage.key] || { count: 0, sum: 0 };
      const isOver = dragOver === stage.key;
      return (
        <div
          key={stage.key}
          onDragOver={(e) => onDragOver(e, stage.key)}
          onDragLeave={onDragLeave}
          onDrop={(e) => onDrop(e, stage.key)}
          style={{
            flex: '0 0 320px',
            background: isOver ? `${stage.color}30` : `${stage.color}08`,
            backdropFilter: 'blur(20px)',
            borderRadius: 20, padding: 14,
            border: isOver ? `2px dashed ${stage.color}` : '1px solid rgba(255,255,255,0.7)',
            display: 'flex', flexDirection: 'column',
            transition: 'all 0.15s',
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 12px', marginBottom: 12,
            background: `${stage.color}12`, borderRadius: 12,
          }}>
            <span style={{ fontSize: 18 }}>{stage.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>{stage.title}</div>
              <div style={{ fontSize: 10, color: '#94a3b8' }}>{stage.probability}%</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ background: stage.color, color: '#fff', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>
                {t.count}
              </div>
              {t.sum > 0 && <div style={{ fontSize: 10, color: '#64748b', marginTop: 3 }}>{formatMoney(t.sum)} ₽</div>}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '65vh', overflowY: 'auto', paddingRight: 4 }}>
            {allDeals.length === 0 ? (
              <div style={{
                padding: 30, textAlign: 'center', color: '#cbd5e1',
                fontSize: 12, fontStyle: 'italic', border: '2px dashed #e2e8f0', borderRadius: 12,
              }}>
                Перетащите сюда
              </div>
            ) : (
              <>
                {visibleDeals.map((d: Deal) => (
                  <DealCard
                    key={d._id} deal={d} accentColor={stage.color}
                    isDragging={dragging === d._id}
                    onClick={() => onDealClick(d)}
                    onDragStart={onDragStart}
                    onDelete={onDelete}
                    canDelete={typeof canDeleteDeal === 'function' ? canDeleteDeal(d) : true}
                  />
                ))}
                {!isExpanded && hiddenCount > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onToggleColumn?.(stage.key); }}
                    style={{
                      width: '100%', padding: '8px 12px', marginTop: 4,
                      background: stage.color + '15', border: '1px dashed ' + stage.color + '80',
                      borderRadius: 10, cursor: 'pointer',
                      fontSize: 12, fontWeight: 600, color: stage.color,
                      fontFamily: 'inherit',
                    }}
                  >
                    + Показать ещё {hiddenCount}
                  </button>
                )}
              {isExpanded && allDeals.length > MAX_VISIBLE_DEALS && (
                <button
                  onClick={(e) => { e.stopPropagation(); onToggleColumn?.(stage.key); }}
                  style={{
                    width: '100%', padding: '8px 12px', marginTop: 4,
                    background: '#f1f5f9', border: '1px solid #e2e8f0',
                    borderRadius: 10, cursor: 'pointer',
                    fontSize: 12, fontWeight: 600, color: '#64748b',
                    fontFamily: 'inherit',
                  }}
                >
                  ↑ Свернуть
                </button>
              )}
              </>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

const DealCard: React.FC<any> = ({ deal, accentColor, isDragging, onClick, onDragStart, onDelete, canDelete }) => {
  const p = PRIORITY_META[deal.priority] || PRIORITY_META.normal;
  const PriorityIcon = p.icon;
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, deal._id)}
      onClick={onClick}
      style={{
        padding: 12, background: 'rgba(255,255,255,0.95)',
        border: `1px solid ${accentColor}20`,
        borderLeft: `3px solid ${accentColor}`,
        borderRadius: 12, cursor: isDragging ? 'grabbing' : 'grab',
        opacity: isDragging ? 0.4 : 1,
        transition: 'all 0.15s',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        if (!isDragging) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = `0 8px 20px ${accentColor}20`;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
      }}
    >
      {deal.isNew && (
        <div style={{
          position: 'absolute', top: 6, right: 6,
          background: '#ef4444', color: '#fff',
          fontSize: 9, fontWeight: 800, padding: '2px 7px',
          borderRadius: 8, letterSpacing: 0.3,
          boxShadow: '0 2px 6px rgba(239,68,68,0.3)',
          animation: 'pulse 2s infinite', zIndex: 2,
        }}>● НОВОЕ</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, marginBottom: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.3, flex: 1 }}>
          {deal.title}
          {(deal.unreadEmailCount || 0) > 0 && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              background: '#ef4444', color: '#fff',
              borderRadius: 10, fontSize: 10, fontWeight: 700,
              padding: '2px 7px', marginLeft: 6, verticalAlign: 'middle',
            }}>
              📧 {deal.unreadEmailCount}
            </span>
          )}
        </div>
        <PriorityIcon size={14} color={p.color} />
      </div>
      {deal.clientCompany && (
        <div style={{ fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
          <Building2 size={11} /> {deal.clientCompany}
        </div>
      )}
      {deal.processStatus === 'in_progress' && (
        <div style={{
          fontSize: 10, padding: '2px 8px', borderRadius: 8,
          background: '#f59e0b20', color: '#f59e0b', fontWeight: 700,
          display: 'inline-flex', alignItems: 'center', gap: 3, marginBottom: 6,
        }}>
          🔔 На согласовании
        </div>
      )}
      {deal.processStatus === 'rework' && (
        <div style={{
          fontSize: 10, padding: '2px 8px', borderRadius: 8,
          background: '#ef444420', color: '#ef4444', fontWeight: 700,
          display: 'inline-flex', alignItems: 'center', gap: 3, marginBottom: 6,
        }}>
          ↩️ На доработке
        </div>
      )}
      {deal.processStatus === 'approved' && (
        <div style={{
          fontSize: 10, padding: '2px 8px', borderRadius: 8,
          background: '#10b98120', color: '#10b981', fontWeight: 700,
          display: 'inline-flex', alignItems: 'center', gap: 3, marginBottom: 6,
        }}>
          ✅ Согласовано
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#10b981' }}>{formatMoney(deal.amount)} ₽</div>
      </div>
      {deal.expectedCloseDate && (
        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
          <Calendar size={10} /> {new Date(deal.expectedCloseDate).toLocaleDateString('ru-RU')}
        </div>
      )}
    </div>
  );
};

// ─── LIST ───
const DealsTable: React.FC<any> = ({ deals, stages, onDealClick, onDelete }) => {
  const stageMap = Object.fromEntries(stages.map((s: Stage) => [s.key, s]));
  if (deals.length === 0) {
    return (
      <div style={emptyState}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>💼</div>
        <h3 style={{ margin: '0 0 8px', color: '#1a1a2e' }}>Сделок не найдено</h3>
      </div>
    );
  }
  return (
    <div style={{
      background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(20px)',
      borderRadius: 18, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.7)',
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead style={{ background: 'rgba(37,117,252,0.04)' }}>
          <tr>
            <th style={th}>Сделка</th>
            <th style={th}>Клиент</th>
            <th style={th}>Сумма</th>
            <th style={th}>Этап</th>
            <th style={th}>Ответственный</th>
            <th style={th}></th>
          </tr>
        </thead>
        <tbody>
          {deals.map((d: Deal) => {
            const stage = stageMap[d.stageKey] as Stage | undefined;
            return (
              <tr key={d._id} style={{ borderTop: '1px solid #f1f5f9' }}>
                <td style={td} onClick={() => onDealClick(d)}>
                  <div style={{ fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {d.title}
                    {(d.unreadEmailCount || 0) > 0 && (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                        background: '#ef4444', color: '#fff',
                        borderRadius: 10, fontSize: 10, fontWeight: 700,
                        padding: '2px 7px',
                      }}>
                        📧 {d.unreadEmailCount}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>{SOURCE_LABELS[d.source || ''] || ''}</div>
                </td>
                <td style={td}>{d.clientCompany || d.clientName || '—'}</td>
                <td style={{ ...td, fontWeight: 800, color: '#10b981' }}>{formatMoney(d.amount)} ₽</td>
                <td style={td}>
                  {stage && (
                    <span style={{
                      padding: '4px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                      background: `${stage.color}15`, color: stage.color,
                    }}>
                      {stage.icon} {stage.title}
                    </span>
                  )}
                </td>
                <td style={td}>{d.ownerName || '—'}</td>
                <td style={td}>
                  <button onClick={() => onDelete(d._id)} style={btnIconSmall}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── DEAL DETAIL MODAL ───
const DealDetailModal: React.FC<any> = ({ dealId, stages, onClose, onUpdate, onUnreadChange }) => {
  const { showToast } = useToast();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'info' | 'meeting' | 'tasks' | 'documents' | 'activity' | 'email'>('info');
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [comment, setComment] = useState('');
  const [newTask, setNewTask] = useState({ title: '', type: 'call', dueDate: '' });
  const [processes, setProcesses] = useState<any[]>([]);
  const [selectedProcess, setSelectedProcess] = useState('');
  const [processRun, setProcessRun] = useState<any>(null);

  // ★ Режим редактирования
  const [isEditing, setIsEditing] = useState(false);
  const [resubmitOpen, setResubmitOpen] = useState(false);
  const [resubmitComment, setResubmitComment] = useState('');
  const [resubmitAttachments, setResubmitAttachments] = useState<any[]>([]);
  const [resubmitLoading, setResubmitLoading] = useState(false);
  const [accountingDocs, setAccountingDocs] = useState<any[]>([]);
  const [creatingDoc, setCreatingDoc] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [unreadEmailCount, setUnreadEmailCount] = useState(0);

  const load = useCallback(async () => {
    try {
      const [r, p, rec] = await Promise.all([
        api.get(`/sales/deals/${dealId}`),
        api.get('/sales/processes').catch(() => ({ data: { items: [] } })),
        api.get(`/sales/automation/nba/deal/${dealId}`).catch(() => ({ data: { items: [] } })),
      ]);
      setData(r.data);
      setProcesses(p.data.items || []);
      setRecommendations(rec.data.items || []);
      // ★ processRun из ответа
      if (r.data.processRun) setProcessRun(r.data.processRun);
      // ★ unreadEmailCount из сделки
      setUnreadEmailCount(r.data.deal?.unreadEmailCount || 0);
    } catch (e: any) { showToast('Ошибка', 'error'); }
    finally { setLoading(false); }
  }, [dealId, showToast]);

  useEffect(() => { load(); }, [load]);

  const handleMove = async (stageKey: string) => {
    try {
      await api.post(`/sales/deals/${dealId}/move`, { stageKey });
      await load();
      onUpdate();
      showToast('Перемещено', 'success');
    } catch (e: any) { showToast('Ошибка', 'error'); }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    await api.post(`/sales/deals/${dealId}/comment`, { text: comment });
    setComment(''); await load();
  };

  const handleAddTask = async () => {
    if (!newTask.title.trim()) return;
    await api.post('/sales/tasks', { ...newTask, dealId });
    setNewTask({ title: '', type: 'call', dueDate: '' });
    await load();
  };

  const handleCompleteTask = async (id: string) => {
    await api.put(`/sales/tasks/${id}`, { status: 'done' });
    await load();
  };

  const handleStartProcess = async () => {
    if (!selectedProcess) return;
    try {
      await api.post(`/sales/processes/${selectedProcess}/start`, { dealId });
      showToast('✅ Процесс запущен', 'success');
      await load(); onUpdate();
    } catch (e: any) { showToast(e?.response?.data?.error || 'Ошибка', 'error'); }
  };

  // ★ Начать редактирование
  const startEdit = () => {
    setEditForm({
      title: deal.title || '',
      clientName: deal.clientName || '',
      clientCompany: deal.clientCompany || '',
      clientEmail: deal.clientEmail || '',
      clientPhone: deal.clientPhone || '',
      amount: deal.amount || 0,
      probability: deal.probability || 0,
      priority: deal.priority || 'normal',
      source: deal.source || 'other',
      expectedCloseDate: deal.expectedCloseDate ? deal.expectedCloseDate.split('T')[0] : '',
      notes: deal.notes || '',
    });
    setIsEditing(true);
  };

  // ★ Сохранить изменения
  const saveEdit = async () => {
    if (!editForm.title?.trim()) {
      showToast('Введите название', 'error');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/sales/deals/${dealId}`, {
        ...editForm,
        amount: Number(editForm.amount) || 0,
        probability: Number(editForm.probability) || 0,
      });
      showToast('✅ Сохранено', 'success');
      setIsEditing(false);
      await load();
      onUpdate();
    } catch (e: any) {
      showToast(e?.response?.data?.error || 'Ошибка', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ★ Отмена
  const cancelEdit = () => {
    setIsEditing(false);
    setEditForm({});
  };

  // ★ Документы
  const loadAccountingDocs = async () => {
    try {
      const r = await api.get(`/sales/deals/${dealId}/accounting-documents`);
      setAccountingDocs(r.data.items || []);
    } catch (e) { /* ignore */ }
  };

  const handleCreateAccountingDoc = async (docType: 'act' | 'upd' | 'torg12') => {
    setCreatingDoc(docType);
    try {
      const r = await api.post(`/sales/deals/${dealId}/create-document`, { docType });
      showToast(`✅ ${r.data.number} создан`, 'success');
      await loadAccountingDocs();
    } catch (e: any) {
      showToast(e?.response?.data?.error || 'Ошибка создания', 'error');
    } finally { setCreatingDoc(null); }
  };

  const handleUploadDocuments = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.size > 10 * 1024 * 1024) {
          showToast(`«${file.name}» больше 10 МБ — пропущен`, 'error');
          continue;
        }
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result));
          reader.onerror = () => reject(new Error('read error'));
          reader.readAsDataURL(file);
        });
        await api.post(`/sales/deals/${dealId}/documents`, {
          name: file.name,
          size: file.size,
          mimeType: file.type || 'application/octet-stream',
          dataUrl,
        });
      }
      showToast(`✅ Загружено файлов: ${files.length}`, 'success');
      await load();
      onUpdate();
    } catch (e: any) {
      showToast(e?.response?.data?.error || 'Ошибка загрузки', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocument = async (doc: any) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/sales/deals/${dealId}/documents/${doc._id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      showToast('Ошибка скачивания', 'error');
    }
  };

  const handleDeleteDocument = async (doc: any) => {
    if (!window.confirm(`Удалить «${doc.name}»?`)) return;
    try {
      await api.delete(`/sales/deals/${dealId}/documents/${doc._id}`);
      showToast('✅ Удалено', 'success');
      await load();
      onUpdate();
    } catch (e: any) {
      showToast(e?.response?.data?.error || 'Ошибка', 'error');
    }
  };

  const formatSize = (bytes: number) => {
    if (!bytes) return '0 Б';
    if (bytes < 1024) return bytes + ' Б';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' КБ';
    return (bytes / (1024 * 1024)).toFixed(1) + ' МБ';
  };

  if (loading || !data) {
    return (
      <div style={modalOverlay} onClick={onClose}>
        <div style={{ ...modalContent, maxWidth: 800, padding: 60, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
          <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      </div>
    );
  }

  const { deal, tasks = [], activities = [] } = data;
  const currentStage = stages.find((s: Stage) => s.key === deal.stageKey);
  const p = PRIORITY_META[deal.priority] || PRIORITY_META.normal;

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{ ...modalContent, maxWidth: 900 }} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeader}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
              {currentStage?.icon} {currentStage?.title}
            </div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#1a1a2e' }}>{deal.title}</h2>
            <div style={{ marginTop: 6, display: 'flex', gap: 16, fontSize: 13, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 800, color: '#10b981', fontSize: 18 }}>{formatMoney(deal.amount)} ₽</span>
              <span style={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
                <p.icon size={13} color={p.color} /> {p.label}
              </span>
              <span style={{ color: '#64748b' }}>Вероятность: {deal.probability}%</span>
            </div>
          </div>
          <button onClick={onClose} style={btnIcon}><X size={20} /></button>
        </div>

        <div style={{ display: 'flex', gap: 4, padding: '10px 24px', borderBottom: '1px solid #f1f5f9', background: 'rgba(248,250,252,0.7)' }}>
          {(['info', 'meeting', 'tasks', 'documents', 'activity', 'email'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)} style={tabBtn(tab === t)}>
              {t === 'info' ? '📋 Инфо'
                : t === 'meeting' ? '📅 Встреча'
                : t === 'tasks' ? `✅ Задачи (${tasks.filter((x: any) => x.status !== 'done').length})`
                : t === 'documents' ? `📎 Документы (${(deal.attachments || []).length})`
                : t === 'email' ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      📧 Почта
                      {unreadEmailCount > 0 && (
                        <span style={{
                          background: '#ef4444', color: '#fff', borderRadius: 10,
                          fontSize: 10, fontWeight: 700, padding: '2px 7px',
                          lineHeight: 1.2,
                        }}>{unreadEmailCount}</span>
                      )}
                    </span>
                  )
                : `💬 Активности (${activities.length})`}
            </button>
          ))}
        </div>

        <div style={{ padding: 24, overflowY: 'auto', flex: 1, maxHeight: 'calc(92vh - 280px)', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {tab === 'info' && (
            <>
              {/* ★ Умные подсказки (NBA) */}
              {recommendations.length > 0 && (
                <RecommendationsPanel
                  items={recommendations}
                  dealId={dealId}
                  onDismiss={async (id: string) => {
                    try {
                      await api.post(`/sales/automation/nba/${id}/dismiss`);
                      setRecommendations((rs: any[]) => rs.filter((r: any) => r._id !== id));
                    } catch (e) { /* ignore */ }
                  }}
                  onCreateTask={async (rec: any) => {
                    try {
                      await api.post('/sales/tasks', {
                        dealId,
                        title: rec.suggestedAction?.params?.title || rec.title,
                        description: rec.description,
                        type: rec.suggestedAction?.params?.taskType || 'call',
                        priority: rec.suggestedAction?.params?.priority || 'normal',
                        dueDate: new Date(Date.now() + 86400000).toISOString(),
                      });
                      showToast('✅ Задача создана', 'success');
                      await api.post(`/sales/automation/nba/${rec._id}/complete`);
                      setRecommendations((rs: any[]) => rs.filter((r: any) => r._id !== rec._id));
                    } catch (e) {
                      showToast('Ошибка создания задачи', 'error');
                    }
                  }}
                />
              )}
              {isEditing ? (
                /* ─── РЕЖИМ РЕДАКТИРОВАНИЯ ─── */
                <>
                  <div>
                    <label style={labelText}>Название *</label>
                    <input
                      value={editForm.title}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                      style={inputStyle}
                      autoFocus
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={labelText}>Контакт</label>
                      <input
                        value={editForm.clientName}
                        onChange={(e) => setEditForm({ ...editForm, clientName: e.target.value })}
                        style={inputStyle}
                        placeholder="Иван Петров"
                      />
                    </div>
                    <div>
                      <label style={labelText}>Компания</label>
                      <input
                        value={editForm.clientCompany}
                        onChange={(e) => setEditForm({ ...editForm, clientCompany: e.target.value })}
                        style={inputStyle}
                        placeholder="ООО Ромашка"
                      />
                    </div>
                    <div>
                      <label style={labelText}>Email</label>
                      <input
                        value={editForm.clientEmail}
                        onChange={(e) => setEditForm({ ...editForm, clientEmail: e.target.value })}
                        style={inputStyle}
                        placeholder="client@company.ru"
                      />
                    </div>
                    <div>
                      <label style={labelText}>Телефон</label>
                      <input
                        value={editForm.clientPhone}
                        onChange={(e) => setEditForm({ ...editForm, clientPhone: e.target.value })}
                        style={inputStyle}
                        placeholder="+7..."
                      />
                    </div>
                    <div>
                      <label style={labelText}>Сумма, ₽</label>
                      <input
                        type="number"
                        value={editForm.amount}
                        onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelText}>Вероятность, %</label>
                      <input
                        type="number"
                        value={editForm.probability}
                        onChange={(e) => setEditForm({ ...editForm, probability: e.target.value })}
                        min={0}
                        max={100}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelText}>Приоритет</label>
                      <select
                        value={editForm.priority}
                        onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                        style={inputStyle}
                      >
                        <option value="low">Низкий</option>
                        <option value="normal">Обычный</option>
                        <option value="high">Высокий</option>
                        <option value="urgent">Срочный</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelText}>Источник</label>
                      <select
                        value={editForm.source}
                        onChange={(e) => setEditForm({ ...editForm, source: e.target.value })}
                        style={inputStyle}
                      >
                        {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                    <div style={{ gridColumn: 'span 2' }}>
                      <label style={labelText}>Ожидаемое закрытие</label>
                      <input
                        type="date"
                        value={editForm.expectedCloseDate}
                        onChange={(e) => setEditForm({ ...editForm, expectedCloseDate: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={labelText}>Заметки</label>
                    <textarea
                      value={editForm.notes}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      rows={3}
                      style={{ ...inputStyle, resize: 'vertical' }}
                    />
                  </div>
                </>
              ) : (
                /* ─── РЕЖИМ ПРОСМОТРА ─── */
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {deal.clientCompany && <InfoBlock label="🏢 Компания" value={deal.clientCompany} />}
                    {deal.clientName && <InfoBlock label="👤 Контакт" value={deal.clientName} />}
                    {deal.clientEmail && <InfoBlock label="📧 Email" value={deal.clientEmail} />}
                    {deal.clientPhone && <InfoBlock label="📞 Телефон" value={deal.clientPhone} />}
                    {deal.expectedCloseDate && <InfoBlock label="📅 Ожид. закрытие" value={new Date(deal.expectedCloseDate).toLocaleDateString('ru-RU')} />}
                    {deal.ownerName && <InfoBlock label="🎯 Ответственный" value={deal.ownerName} />}
                    {deal.source && <InfoBlock label="📌 Источник" value={SOURCE_LABELS[deal.source] || deal.source} />}
                    <InfoBlock label="💰 Сумма" value={(deal.amount || 0).toLocaleString('ru-RU') + ' ₽'} />
                  </div>

                  <div>
                    <div style={sectionTitle}>Переместить в этап</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                      {stages.map((s: Stage) => (
                        <button key={s.key} onClick={() => handleMove(s.key)} disabled={s.key === deal.stageKey}
                          style={{
                            padding: '8px 14px', borderRadius: 10,
                            border: s.key === deal.stageKey ? `2px solid ${s.color}` : '1px solid rgba(255,255,255,0.7)',
                            background: s.key === deal.stageKey ? `${s.color}15` : 'rgba(255,255,255,0.7)',
                            color: s.key === deal.stageKey ? s.color : '#475569',
                            cursor: s.key === deal.stageKey ? 'default' : 'pointer',
                            fontSize: 12, fontWeight: 600,
                          }}
                        >{s.icon} {s.title}</button>
                      ))}
                    </div>
                  </div>

                  {/* Бизнес-процесс */}
                  <div>
                    <div style={sectionTitle}>🔔 Бизнес-процесс согласования</div>
                    {(deal.processRunId || processRun) ? (
                      <div style={{
                        padding: 14,
                        background: deal.processStatus === 'rework' ? '#ef444415' :
                                    deal.processStatus === 'approved' ? '#10b98115' :
                                    '#f59e0b15',
                        borderRadius: 12, marginTop: 8,
                      }}>
                        {/* Заголовок */}
                        <div style={{ fontSize: 13, fontWeight: 700,
                          color: deal.processStatus === 'rework' ? '#ef4444' :
                                 deal.processStatus === 'approved' ? '#10b981' :
                                 '#f59e0b'
                        }}>
                          {deal.processStatus === 'rework' ? '↩️ Требуется доработка' :
                           deal.processStatus === 'approved' ? '✅ Согласовано' :
                           deal.processStatus === 'in_progress' ? '⏳ В процессе согласования' :
                           deal.processStatus === 'rejected' ? '❌ Отклонено' : '—'}
                        </div>

                        {/* Процесс + текущий этап */}
                        {processRun && (
                          <>
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                              {processRun.processName}
                            </div>
                            {processRun.currentStage && (
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>{processRun.currentStage.icon}</span>
                                <span>{processRun.currentStage.title}</span>
                                <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>
                                  (этап {processRun.currentStageIdx + 1} из {processRun.totalStages})
                                </span>
                              </div>
                            )}

                            {/* Прогресс-бар */}
                            {processRun.progress && processRun.totalStages > 0 && (
                              <div style={{ marginTop: 10 }}>
                                <div style={{ height: 4, background: 'rgba(255,255,255,0.5)', borderRadius: 2, overflow: 'hidden' }}>
                                  <div style={{
                                    height: 4,
                                    background: deal.processStatus === 'approved' ? '#10b981' : '#f59e0b',
                                    width: `${processRun.progress.percent || 0}%`,
                                    borderRadius: 2,
                                    transition: 'width 0.3s',
                                  }} />
                                </div>
                                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
                                  {processRun.progress.completed || 0} из {processRun.progress.total || 0} этапов пройдено
                                </div>
                              </div>
                            )}

                            {/* Начато */}
                            {processRun.startedByName && (
                              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 6 }}>
                                Запустил: {processRun.startedByName}
                                {processRun.startedAt && (
                                  <> · {new Date(processRun.startedAt).toLocaleString('ru-RU')}</>
                                )}
                              </div>
                            )}

                            {/* Кнопка открыть в Согласованиях */}
                            <div style={{ marginTop: 10 }}>
                              <button
                                onClick={() => { onClose(); window.location.href = '/app/sales/approvals'; }}
                                style={{
                                  background: 'transparent', border: 'none',
                                  color: '#2575fc', cursor: 'pointer',
                                  fontSize: 11, fontWeight: 600, padding: 0,
                                  textDecoration: 'underline',
                                }}
                              >
                                Открыть в Согласованиях →
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <select value={selectedProcess} onChange={(e) => setSelectedProcess(e.target.value)} style={{ ...inputStyle, flex: 1 }}>
                          <option value="">Выберите процесс...</option>
                          {processes.map((p: any) => <option key={p._id} value={p._id}>{p.icon} {p.name}</option>)}
                        </select>
                        <button onClick={handleStartProcess} disabled={!selectedProcess} style={btnPrimary}>Запустить</button>
                      </div>
                    )}
                  </div>

                  {deal.notes && (
                    <div>
                      <div style={sectionTitle}>Заметки</div>
                      <div style={{ padding: 14, background: 'rgba(0,0,0,0.02)', borderRadius: 10, fontSize: 13, color: '#475569', lineHeight: 1.6, marginTop: 8, whiteSpace: 'pre-wrap' }}>
                        {deal.notes}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {tab === 'documents' && (
            <div>
              {/* ★ СОЗДАНИЕ ПЕЧАТНЫХ ДОКУМЕНТОВ */}
              <div style={{
                padding: 16, marginBottom: 16,
                background: 'linear-gradient(135deg, rgba(139,92,246,0.06), rgba(37,117,252,0.06))',
                borderRadius: 14, border: '1px solid rgba(139,92,246,0.15)',
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', marginBottom: 10 }}>
                  📄 Создать печатный документ
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => handleCreateAccountingDoc('act')}
                    disabled={!!creatingDoc}
                    style={{
                      padding: '10px 16px', borderRadius: 10,
                      border: 'none', background: 'linear-gradient(135deg, #10b981, #059669)',
                      color: '#fff', cursor: creatingDoc ? 'wait' : 'pointer',
                      fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      opacity: creatingDoc === 'act' ? 0.6 : 1,
                    }}
                  >
                    {creatingDoc === 'act' ? '⏳...' : '✅ Создать Акт'}
                  </button>
                  <button
                    onClick={() => handleCreateAccountingDoc('upd')}
                    disabled={!!creatingDoc}
                    style={{
                      padding: '10px 16px', borderRadius: 10,
                      border: 'none', background: 'linear-gradient(135deg, #8b5cf6, #6a11cb)',
                      color: '#fff', cursor: creatingDoc ? 'wait' : 'pointer',
                      fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      opacity: creatingDoc === 'upd' ? 0.6 : 1,
                    }}
                  >
                    {creatingDoc === 'upd' ? '⏳...' : '📄 Создать УПД'}
                  </button>
                  <button
                    onClick={() => handleCreateAccountingDoc('torg12')}
                    disabled={!!creatingDoc}
                    style={{
                      padding: '10px 16px', borderRadius: 10,
                      border: 'none', background: 'linear-gradient(135deg, #2575fc, #1e40af)',
                      color: '#fff', cursor: creatingDoc ? 'wait' : 'pointer',
                      fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      opacity: creatingDoc === 'torg12' ? 0.6 : 1,
                    }}
                  >
                    {creatingDoc === 'torg12' ? '⏳...' : '📦 Создать ТОРГ-12'}
                  </button>
                </div>
              </div>

              {/* ★ СПИСОК БУХ. ДОКУМЕНТОВ */}
              {accountingDocs.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>
                    📋 Бухгалтерские документы ({accountingDocs.length})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {accountingDocs.map((d: any) => (
                      <div key={d._id} style={{
                        padding: 10, borderRadius: 10,
                        background: 'rgba(255,255,255,0.7)',
                        border: '1px solid rgba(0,0,0,0.05)',
                        display: 'flex', alignItems: 'center', gap: 10,
                      }}>
                        <span style={{ fontSize: 20 }}>
                          {d.docType === 'act' ? '✅' : d.docType === 'upd' ? '📄' : '📦'}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>
                            {d.number}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>
                            {d.date ? new Date(d.date).toLocaleDateString('ru-RU') : '—'} ·{' '}
                            {d.total ? d.total.toLocaleString('ru-RU') + ' ₽' : ''}
                          </div>
                        </div>
                        <span style={{
                          fontSize: 9, padding: '3px 8px', borderRadius: 6, fontWeight: 700,
                          background: d.signed ? 'rgba(16,185,129,0.1)' : 'rgba(148,163,184,0.15)',
                          color: d.signed ? '#10b981' : '#94a3b8',
                        }}>
                          {d.signed ? '✅ Подписан' : '📝 Черновик'}
                        </span>
                        <a
                          href={`/app/accounting/documents`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontSize: 10, color: '#8b5cf6', textDecoration: 'none',
                            fontWeight: 600, padding: '4px 8px', borderRadius: 6,
                            background: 'rgba(139,92,246,0.08)',
                          }}
                          title="Открыть в бухгалтерии"
                        >
                          →
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Drop zone */}
              <label
                htmlFor="deal-doc-upload"
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  padding: 32, marginBottom: 16,
                  border: '2px dashed rgba(37,117,252,0.3)',
                  borderRadius: 14,
                  background: uploading ? 'rgba(37,117,252,0.06)' : 'rgba(37,117,252,0.02)',
                  cursor: uploading ? 'wait' : 'pointer',
                  transition: 'all 0.15s',
                }}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.background = 'rgba(37,117,252,0.08)'; e.currentTarget.style.borderColor = '#2575fc'; }}
                onDragLeave={(e) => { e.currentTarget.style.background = 'rgba(37,117,252,0.02)'; e.currentTarget.style.borderColor = 'rgba(37,117,252,0.3)'; }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.style.background = 'rgba(37,117,252,0.02)';
                  e.currentTarget.style.borderColor = 'rgba(37,117,252,0.3)';
                  handleUploadDocuments(e.dataTransfer.files);
                }}
              >
                <Upload size={32} color="#2575fc" style={{ marginBottom: 10 }} />
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e' }}>
                  {uploading ? '⏳ Загрузка...' : '📎 Перетащите файлы или кликните для выбора'}
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                  До 10 МБ на файл. PDF, DOCX, XLSX, изображения и др.
                </div>
                <input
                  id="deal-doc-upload"
                  type="file"
                  multiple
                  onChange={(e) => { handleUploadDocuments(e.target.files); e.target.value = ''; }}
                  style={{ display: 'none' }}
                  disabled={uploading}
                />
              </label>

              {/* Список документов */}
              {(deal.attachments || []).length === 0 ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  Документов пока нет — загрузите первый
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {(deal.attachments || []).map((doc: any) => (
                    <div key={doc._id} style={{
                      padding: 12, borderRadius: 12, background: 'rgba(255,255,255,0.7)',
                      border: '1px solid rgba(0,0,0,0.05)',
                      display: 'flex', alignItems: 'center', gap: 12,
                    }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                        background: 'rgba(37,117,252,0.1)', color: '#2575fc',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <FileText size={20} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 13, fontWeight: 700, color: '#1a1a2e',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>{doc.name}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                          {formatSize(doc.size)} · {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleString('ru-RU') : '—'}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownloadDocument(doc)}
                        title="Скачать"
                        style={btnIconSm}
                      >
                        <Download size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteDocument(doc)}
                        title="Удалить"
                        style={{ ...btnIconSm, color: '#ef4444' }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'meeting' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* ─── Секция: Встреча ─── */}
              <div style={sectionCard}>
                <div style={meetingSectionTitle}>📅 Встреча</div>
                <div style={gridTwoCols}>
                  <div>
                    <label style={labelText}>Дата и время</label>
                    <input
                      type="datetime-local"
                      value={editForm.meetingAt ? new Date(editForm.meetingAt).toISOString().slice(0, 16) : ''}
                      onChange={(e) => setEditForm({ ...editForm, meetingAt: e.target.value })}
                      style={inputStyle}
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <label style={labelText}>Место / адрес</label>
                    <input
                      type="text"
                      value={editForm.meetingLocation || ''}
                      onChange={(e) => setEditForm({ ...editForm, meetingLocation: e.target.value })}
                      placeholder="Офис, адрес или онлайн"
                      style={inputStyle}
                      disabled={!isEditing}
                    />
                  </div>
                </div>
                <div style={{ marginTop: 12 }}>
                  <label style={labelText}>Ссылка на встречу (Zoom / Meet / Teams)</label>
                  <input
                    type="url"
                    value={editForm.meetingUrl || ''}
                    onChange={(e) => setEditForm({ ...editForm, meetingUrl: e.target.value })}
                    placeholder="https://..."
                    style={inputStyle}
                    disabled={!isEditing}
                  />
                  {editForm.meetingUrl && (
                    <a href={editForm.meetingUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#2575fc', marginTop: 4, display: 'inline-block' }}>
                      → Открыть ссылку
                    </a>
                  )}
                </div>
                <div style={{ marginTop: 12 }}>
                  <label style={labelText}>Участники (через запятую)</label>
                  <input
                    type="text"
                    value={(editForm.meetingAttendees || []).join(', ')}
                    onChange={(e) => setEditForm({ ...editForm, meetingAttendees: e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean) })}
                    placeholder="ivan@mail.ru, anna@mail.ru"
                    style={inputStyle}
                    disabled={!isEditing}
                  />
                </div>
                <div style={{ marginTop: 12 }}>
                  <label style={labelText}>Заметки по встрече</label>
                  <textarea
                    value={editForm.meetingNotes || ''}
                    onChange={(e) => setEditForm({ ...editForm, meetingNotes: e.target.value })}
                    rows={3}
                    placeholder="О чём говорили, что решили..."
                    style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                    disabled={!isEditing}
                  />
                </div>
              </div>

              {/* ─── Секция: Следующий шаг ─── */}
              <div style={sectionCard}>
                <div style={meetingSectionTitle}>➡️ Следующий шаг</div>
                <div style={gridTwoCols}>
                  <div>
                    <label style={labelText}>Что делать</label>
                    <input
                      type="text"
                      value={editForm.nextStep || ''}
                      onChange={(e) => setEditForm({ ...editForm, nextStep: e.target.value })}
                      placeholder="Позвонить, отправить КП..."
                      style={inputStyle}
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <label style={labelText}>Когда</label>
                    <input
                      type="datetime-local"
                      value={editForm.nextContactAt ? new Date(editForm.nextContactAt).toISOString().slice(0, 16) : ''}
                      onChange={(e) => setEditForm({ ...editForm, nextContactAt: e.target.value })}
                      style={inputStyle}
                      disabled={!isEditing}
                    />
                  </div>
                </div>
              </div>

              {/* ─── Секция: Договор ─── */}
              <div style={sectionCard}>
                <div style={meetingSectionTitle}>📄 Договор</div>
                <div style={gridTwoCols}>
                  <div>
                    <label style={labelText}>Номер договора</label>
                    <input
                      type="text"
                      value={editForm.contractNumber || ''}
                      onChange={(e) => setEditForm({ ...editForm, contractNumber: e.target.value })}
                      placeholder="№ 123/2026"
                      style={inputStyle}
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <label style={labelText}>Дата подписания</label>
                    <input
                      type="date"
                      value={editForm.contractDate ? new Date(editForm.contractDate).toISOString().slice(0, 10) : ''}
                      onChange={(e) => setEditForm({ ...editForm, contractDate: e.target.value })}
                      style={inputStyle}
                      disabled={!isEditing}
                    />
                  </div>
                </div>
                <div style={{ marginTop: 12 }}>
                  <label style={labelText}>Ссылка на документ</label>
                  <input
                    type="url"
                    value={editForm.contractUrl || ''}
                    onChange={(e) => setEditForm({ ...editForm, contractUrl: e.target.value })}
                    placeholder="https://..."
                    style={inputStyle}
                    disabled={!isEditing}
                  />
                </div>
              </div>
            </div>
          )}

          {tab === 'tasks' && (
            <>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder="Новая задача..." style={{ ...inputStyle, flex: 1 }}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddTask()} />
                <input type="date" value={newTask.dueDate} onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })} style={{ ...inputStyle, width: 150 }} />
                <button onClick={handleAddTask} style={btnPrimary}><Plus size={14} /></button>
              </div>
              {tasks.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Задач нет</div>
              ) : (
                tasks.map((t: any) => (
                  <div key={t._id} style={{
                    padding: 12, borderRadius: 12, border: '1px solid #e2e8f0',
                    display: 'flex', alignItems: 'center', gap: 10,
                    opacity: t.status === 'done' ? 0.5 : 1,
                  }}>
                    <button onClick={() => t.status !== 'done' && handleCompleteTask(t._id)}
                      disabled={t.status === 'done'}
                      style={{
                        width: 20, height: 20, borderRadius: 4,
                        border: t.status === 'done' ? 'none' : '1.5px solid #cbd5e1',
                        background: t.status === 'done' ? '#10b981' : 'transparent',
                        cursor: t.status === 'done' ? 'default' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                      {t.status === 'done' && <CheckCircle size={12} color="#fff" />}
                    </button>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 600,
                        textDecoration: t.status === 'done' ? 'line-through' : 'none',
                        color: t.status === 'done' ? '#94a3b8' : '#1a1a2e',
                      }}>{t.title}</div>
                      {t.dueDate && <div style={{ fontSize: 11, color: '#94a3b8' }}>до {new Date(t.dueDate).toLocaleDateString('ru-RU')}</div>}
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          {tab === 'email' && (
            <EmailThreadPanel
              dealId={dealId}
              clientEmail={deal?.clientEmail}
              clientName={deal?.clientName || deal?.clientCompany}
              onUnreadChange={(cnt: number) => {
                setUnreadEmailCount(cnt);
                // ★ Обновляем badge на карточке в списке через callback родителя
                onUnreadChange?.(dealId, cnt);
              }}
            />
          )}

          {tab === 'activity' && (
            <>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={comment} onChange={(e) => setComment(e.target.value)}
                  placeholder="Написать комментарий..." style={{ ...inputStyle, flex: 1 }}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddComment()} />
                <button onClick={handleAddComment} style={btnPrimary}><MessageSquare size={14} /></button>
              </div>
              {activities.map((a: any) => (
                <div key={a._id} style={{ display: 'flex', gap: 12 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 16,
                    background: '#2575fc15', color: '#2575fc',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0,
                  }}>
                    {a.type === 'stage_change' ? '📊' : a.type === 'task_done' ? '✅' : a.type === 'comment' ? '💬' : '📌'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{a.title}</div>
                    {a.description && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, whiteSpace: 'pre-wrap' }}>{a.description}</div>}
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                      {a.userName || '—'} · {new Date(a.createdAt).toLocaleString('ru-RU')}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        <div style={modalFooter}>
          {isEditing ? (
            <>
              <button onClick={cancelEdit} style={btnOutline}>Отмена</button>
              <button
                onClick={saveEdit}
                disabled={saving}
                style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
              >
                {saving ? 'Сохранение...' : '💾 Сохранить'}
              </button>
            </>
          ) : (
            <>
              <button onClick={onClose} style={btnOutline}>Закрыть</button>
              <button
                onClick={startEdit}
                style={{ ...btnOutline, color: '#2575fc', borderColor: '#2575fc40' }}
              >
                ✏️ Редактировать
              </button>

              {!deal.processStatus && (
                <>
                  <button
                    onClick={() => { onClose(); window.dispatchEvent(new CustomEvent('send-deal-to', { detail: deal })); }}
                    style={{ ...btnOutline, color: '#6366f1', borderColor: '#6366f140' }}
                  >
                    ⚖️ Отправить юристам
                  </button>
                  <button
                    onClick={() => { onClose(); window.dispatchEvent(new CustomEvent('send-deal-to', { detail: deal })); }}
                    style={{ ...btnPrimary, background: 'linear-gradient(135deg, #10b981, #059669)' }}
                  >
                    📤 В бухгалтерию
                  </button>
                </>
              )}

              {deal.processStatus === 'in_progress' && (
                <div style={{ padding: '8px 14px', background: '#f59e0b15', borderRadius: 10, fontSize: 12, fontWeight: 600, color: '#f59e0b' }}>
                  ⏳ На согласовании
                </div>
              )}

              {deal.processStatus === 'rework' && (
                <>
                  <div style={{ padding: '8px 14px', background: '#f59e0b15', borderRadius: 10, fontSize: 12, fontWeight: 600, color: '#f59e0b' }}>
                    ↩️ Требуется доработка
                  </div>
                  <button
                    onClick={() => {
                      setResubmitOpen(true);
                      setResubmitComment('');
                      setResubmitAttachments([]);
                    }}
                    style={{ ...btnPrimary, background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}
                  >
                    🔄 Отправить снова юристам
                  </button>
                </>
              )}

              {deal.processStatus === 'approved' && (
                <button
                  onClick={() => { onClose(); window.dispatchEvent(new CustomEvent('send-deal-to', { detail: deal })); }}
                  style={{ ...btnPrimary, background: 'linear-gradient(135deg, #10b981, #059669)' }}
                >
                  📤 Отправить в бухгалтерию
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* ★ Модалка «Отправить снова юристам» */}
      {resubmitOpen && (
        <div style={modalOverlay} onClick={() => setResubmitOpen(false)}>
          <div style={{ ...modalContent, maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader}>
              <div>
                <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Повторная отправка</div>
                <h3 style={{ margin: '4px 0 0', fontSize: 18, fontWeight: 800 }}>🔄 Отправить юристам снова</h3>
              </div>
              <button onClick={() => setResubmitOpen(false)} style={btnIcon}><X size={18} /></button>
            </div>
            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={labelText}>Комментарий * <span style={{ color: '#94a3b8', fontWeight: 400 }}>(обязательно)</span></label>
                <textarea
                  value={resubmitComment}
                  onChange={(e) => setResubmitComment(e.target.value)}
                  rows={4}
                  placeholder="Опишите какие исправления внесены..."
                  style={{ ...inputStyle, resize: 'vertical' }}
                  autoFocus
                />
              </div>
              <div>
                <label style={labelText}>Файлы (опционально)</label>
                <input
                  type="file"
                  multiple
                  onChange={(e) => {
                    const files = e.target.files;
                    if (!files) return;
                    Array.from(files).forEach(file => {
                      if (file.size > 10 * 1024 * 1024) return;
                      const reader = new FileReader();
                      reader.onload = () => {
                        setResubmitAttachments((prev: any[]) => [...prev, {
                          name: file.name, size: file.size,
                          mimeType: file.type, dataUrl: reader.result as string,
                        }]);
                      };
                      reader.readAsDataURL(file);
                    });
                    e.target.value = '';
                  }}
                  style={{ fontSize: 12 }}
                />
                {resubmitAttachments.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {resubmitAttachments.map((att: any, i: number) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 6, background: '#f8fafc', borderRadius: 6, fontSize: 11 }}>
                        <FileText size={11} />
                        <span style={{ flex: 1 }}>{att.name}</span>
                        <button onClick={() => setResubmitAttachments((prev: any[]) => prev.filter((_, x) => x !== i))}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2 }}>
                          <X size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={modalFooter}>
              <button onClick={() => setResubmitOpen(false)} style={btnOutline}>Отмена</button>
              <button
                onClick={async () => {
                  if (!resubmitComment.trim()) { showToast('Напишите комментарий', 'error'); return; }
                  setResubmitLoading(true);
                  try {
                    const r = await api.get('/sales/process-runs?status=on_hold');
                    const myRun = r.data.items?.find((x: any) => String(x.dealId) === String(deal._id));
                    if (!myRun) { showToast('Активный процесс не найден', 'error'); return; }

                    await api.post(`/sales/process-runs/${myRun._id}/resubmit`, {
                      comment: resubmitComment,
                      attachments: resubmitAttachments.map((a: any) => ({ name: a.name, size: a.size, mimeType: a.mimeType, url: a.dataUrl })),
                    });
                    showToast('🔄 Отправлено снова', 'success');
                    setResubmitOpen(false);
                    setResubmitComment('');
                    setResubmitAttachments([]);
                    load(); onUpdate();
                  } catch (e: any) {
                    showToast(e?.response?.data?.error || 'Ошибка', 'error');
                  } finally {
                    setResubmitLoading(false);
                  }
                }}
                disabled={!resubmitComment.trim() || resubmitLoading}
                style={{
                  ...btnPrimary,
                  background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                  opacity: !resubmitComment.trim() || resubmitLoading ? 0.5 : 1,
                  cursor: !resubmitComment.trim() || resubmitLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {resubmitLoading ? 'Отправка...' : '🔄 Отправить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── CREATE MODAL ───
const CreateDealModal: React.FC<any> = ({ pipeline, onClose, onCreated }) => {
  const { showToast } = useToast();
  const [form, setForm] = useState({
    title: '', clientName: '', clientCompany: '', clientEmail: '', clientPhone: '',
    amount: 0, expectedCloseDate: '', priority: 'normal', source: 'other', notes: '',
  });
  const [creating, setCreating] = useState(false);

  const submit = async () => {
    if (!form.title.trim()) { showToast('Введите название', 'error'); return; }
    setCreating(true);
    try {
      await api.post('/sales/deals', { ...form, pipelineId: pipeline?._id, amount: Number(form.amount) || 0 });
      showToast('✅ Сделка создана', 'success');
      onCreated();
    } catch (e: any) { showToast(e?.response?.data?.error || 'Ошибка', 'error'); }
    finally { setCreating(false); }
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{ ...modalContent, maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeader}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>💼 Новая сделка</h3>
          <button onClick={onClose} style={btnIcon}><X size={18} /></button>
        </div>
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' }}>
          <div>
            <label style={labelText}>Название *</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Внедрение..." style={inputStyle} autoFocus />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={labelText}>Клиент</label><input value={form.clientName} onChange={(e) => setForm({ ...form, clientName: e.target.value })} style={inputStyle} /></div>
            <div><label style={labelText}>Компания</label><input value={form.clientCompany} onChange={(e) => setForm({ ...form, clientCompany: e.target.value })} style={inputStyle} /></div>
            <div><label style={labelText}>Email</label><input value={form.clientEmail} onChange={(e) => setForm({ ...form, clientEmail: e.target.value })} style={inputStyle} /></div>
            <div><label style={labelText}>Телефон</label><input value={form.clientPhone} onChange={(e) => setForm({ ...form, clientPhone: e.target.value })} style={inputStyle} /></div>
            <div><label style={labelText}>Сумма, ₽</label><input type="number" value={form.amount || ''} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} style={inputStyle} /></div>
            <div><label style={labelText}>Ожид. закрытие</label><input type="date" value={form.expectedCloseDate} onChange={(e) => setForm({ ...form, expectedCloseDate: e.target.value })} style={inputStyle} /></div>
            <div>
              <label style={labelText}>Приоритет</label>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} style={inputStyle}>
                <option value="low">Низкий</option><option value="normal">Обычный</option>
                <option value="high">Высокий</option><option value="urgent">Срочный</option>
              </select>
            </div>
            <div>
              <label style={labelText}>Источник</label>
              <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} style={inputStyle}>
                {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={labelText}>Заметки</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
        </div>
        <div style={modalFooter}>
          <button onClick={onClose} style={btnOutline}>Отмена</button>
          <button onClick={submit} disabled={creating} style={{ ...btnPrimary, opacity: creating ? 0.6 : 1 }}>
            {creating ? 'Создание...' : 'Создать'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── CUSTOM BOARD VIEW ───
const CustomBoardView: React.FC<{ boardData: any; onDealClick: (d: any) => void }> = ({ boardData, onDealClick }) => {
  const { board, boardData: columns } = boardData;

  if (!board || !columns) return <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>Нет данных</div>;

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 24 }}>{board.icon || '📊'}</span>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#1a1a2e' }}>{board.name}</div>
          {board.description && <div style={{ fontSize: 12, color: '#64748b' }}>{board.description}</div>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 12, alignItems: 'flex-start' }}>
        {board.columns.map((col: any) => {
          const deals = columns[col.key] || [];
          const sum = deals.reduce((acc: number, d: any) => acc + (d.amount || 0), 0);
          return (
            <div
              key={col.key}
              style={{
                flex: '0 0 320px',
                background: 'rgba(255,255,255,0.55)',
                backdropFilter: 'blur(20px)',
                borderRadius: 20, padding: 14,
                border: `2px solid ${col.color || '#2575fc'}30`,
                display: 'flex', flexDirection: 'column',
              }}
            >
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 12px', marginBottom: 12,
                background: `${col.color || '#2575fc'}12`, borderRadius: 12,
              }}>
                <span style={{ fontSize: 18 }}>{col.icon || '📌'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>{col.title}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ background: col.color || '#2575fc', color: '#fff', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>
                    {deals.length}
                  </div>
                  {sum > 0 && <div style={{ fontSize: 10, color: '#64748b', marginTop: 3 }}>{sum.toLocaleString('ru-RU')} ₽</div>}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {deals.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: '#cbd5e1', fontSize: 12, fontStyle: 'italic' }}>Пусто</div>
                ) : (
                  deals.map((d: any) => (
                    <div
                      key={d._id}
                      onClick={() => onDealClick(d)}
                      style={{
                        padding: 12, background: 'rgba(255,255,255,0.95)',
                        border: `1px solid ${col.color || '#2575fc'}20`,
                        borderLeft: `3px solid ${col.color || '#2575fc'}`,
                        borderRadius: 12, cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = `0 8px 20px ${col.color || '#2575fc'}20`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', marginBottom: 6, lineHeight: 1.3 }}>
                        {d.title}
                      </div>
                      {d.clientCompany && (
                        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>🏢 {d.clientCompany}</div>
                      )}
                      <div style={{ fontSize: 16, fontWeight: 800, color: '#10b981' }}>
                        {(d.amount || 0).toLocaleString('ru-RU')} ₽
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const InfoBlock: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ padding: 12, background: 'rgba(0,0,0,0.02)', borderRadius: 10 }}>
    <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 13, color: '#1a1a2e', fontWeight: 600 }}>{value}</div>
  </div>
);

// ─── STYLES ───
function formatMoney(n: number): string { return (n || 0).toLocaleString('ru-RU'); }

const sectionCard: React.CSSProperties = {
  padding: 16,
  borderRadius: 14,
  background: 'rgba(255,255,255,0.5)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.7)',
};
const meetingSectionTitle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  color: '#1a1a2e',
  marginBottom: 12,
};
const gridTwoCols: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px', borderRadius: 12,
  border: '1px solid #e2e8f0', fontSize: 13, background: 'rgba(255,255,255,0.8)',
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
const btnPrimary: React.CSSProperties = {
  background: 'linear-gradient(135deg, #2575fc, #6a11cb)',
  color: '#fff', border: 'none', borderRadius: 12, padding: '10px 18px',
  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
  fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
  boxShadow: '0 4px 12px rgba(37,117,252,0.25)',
};
const btnOutline: React.CSSProperties = {
  background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.8)', borderRadius: 12,
  padding: '10px 16px', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 6,
  fontSize: 13, fontWeight: 600, color: '#475569', fontFamily: 'inherit',
};
const btnIcon: React.CSSProperties = {
  background: 'rgba(255,255,255,0.85)', border: '1px solid #e2e8f0',
  borderRadius: 12, padding: 10, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const btnIconSmall: React.CSSProperties = {
  background: 'rgba(239,68,68,0.08)', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', color: '#ef4444',
};
const btnIconSm: React.CSSProperties = btnIconSmall;
const viewBtn = (active: boolean): React.CSSProperties => ({
  padding: '8px 14px', borderRadius: 9, border: 'none',
  background: active ? 'linear-gradient(135deg, #2575fc, #6a11cb)' : 'transparent',
  color: active ? '#fff' : '#64748b',
  cursor: 'pointer', fontSize: 12, fontWeight: 600,
  display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'inherit',
});
const tabBtn = (active: boolean): React.CSSProperties => ({
  padding: '8px 16px', borderRadius: 8, border: 'none',
  background: active ? 'rgba(37,117,252,0.1)' : 'transparent',
  color: active ? '#2575fc' : '#64748b',
  cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
});
const th: React.CSSProperties = { padding: '14px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 };
const td: React.CSSProperties = { padding: '14px 16px', fontSize: 13, color: '#1a1a2e' };
const labelText: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 };
const sectionTitle: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 };
const emptyState: React.CSSProperties = { padding: 60, background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(20px)', borderRadius: 20, textAlign: 'center', border: '1px solid rgba(255,255,255,0.7)' };
const modalOverlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: 20 };
const modalContent: React.CSSProperties = { background: '#fff', borderRadius: 20, maxWidth: 800, width: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 60px rgba(15,23,42,0.3)', overflow: 'hidden' };
const modalHeader: React.CSSProperties = { padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 };
const modalFooter: React.CSSProperties = { padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 10, justifyContent: 'flex-end', background: '#f8fafc' };


// ─── Умные подсказки (Next Best Action) ───
const RecommendationsPanel: React.FC<{
  items: any[];
  dealId: string;
  onDismiss: (id: string) => void;
  onCreateTask: (rec: any) => void;
}> = ({ items, onDismiss, onCreateTask }) => {
  const priorityColor: any = {
    urgent: '#ef4444',
    high: '#f59e0b',
    normal: '#2575fc',
    low: '#94a3b8',
  };
  const priorityLabel: any = {
    urgent: 'СРОЧНО',
    high: 'ВАЖНО',
    normal: 'ОБЫЧНО',
    low: 'НИЗКИЙ',
  };

  return (
    <div style={{
      marginBottom: 16, padding: 14,
      background: 'linear-gradient(135deg, rgba(139,92,246,0.06), rgba(37,117,252,0.06))',
      borderRadius: 14, border: '1px solid rgba(139,92,246,0.15)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 12, fontWeight: 800, color: '#8b5cf6',
        marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.3,
      }}>
        🧠 Умные подсказки ({items.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map(rec => (
          <div key={rec._id} style={{
            padding: 10, background: '#fff', borderRadius: 10,
            border: `1px solid ${priorityColor[rec.priority] || '#e2e8f0'}30`,
            display: 'flex', alignItems: 'flex-start', gap: 10,
          }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>{rec.icon || '💡'}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e' }}>
                  {rec.title}
                </span>
                <span style={{
                  fontSize: 8, padding: '1px 6px', borderRadius: 4, fontWeight: 800,
                  background: `${priorityColor[rec.priority]}18`,
                  color: priorityColor[rec.priority],
                }}>
                  {priorityLabel[rec.priority]}
                </span>
              </div>
              {rec.description && (
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>
                  {rec.description}
                </div>
              )}
              <div style={{ display: 'flex', gap: 4 }}>
                {rec.suggestedAction?.type === 'create_task' && (
                  <button onClick={() => onCreateTask(rec)} style={{
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    color: '#fff', border: 'none', borderRadius: 6,
                    padding: '4px 10px', cursor: 'pointer', fontSize: 10, fontWeight: 700,
                    fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}>
                    ✅ {rec.suggestedAction.label || 'Выполнить'}
                  </button>
                )}
                <button onClick={() => onDismiss(rec._id)} style={{
                  background: 'transparent', color: '#94a3b8',
                  border: '1px solid #e2e8f0', borderRadius: 6,
                  padding: '4px 10px', cursor: 'pointer', fontSize: 10, fontWeight: 600,
                  fontFamily: 'inherit',
                }}>
                  Скрыть
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DealsPage;
